import { useState, useEffect } from 'react';
import { useOrg } from '../../contexts/OrgContext';
import { usePermission } from '../../hooks/usePermission';
import {
  getMeasurementDefinitions,
  deleteMeasurementDefinition,
} from '../../services/measurementDefinitionService';
import { backfillMeasurementsFromInspections } from '../../services/inspectionService';
import { MeasurementDefinition } from '@easydone/shared';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { MeasurementDefinitionDialog } from '../../components/MeasurementDefinitionDialog';

export function MeasurementDefinitionsPage() {
  const { currentOrg } = useOrg();
  const canManage = usePermission('templates.create');

  const [definitions, setDefinitions] = useState<MeasurementDefinition[]>([]);
  const [filteredDefinitions, setFilteredDefinitions] = useState<MeasurementDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedContext, setSelectedContext] = useState<string>('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<MeasurementDefinition | undefined>();

  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{
    processedCount: number;
    measurementsRecorded: number;
    errors: Array<{ inspectionId: string; error: string }>;
  } | null>(null);

  useEffect(() => {
    loadDefinitions();
  }, [currentOrg]);

  useEffect(() => {
    filterDefinitions();
  }, [searchQuery, selectedCategory, selectedContext, definitions]);

  const loadDefinitions = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getMeasurementDefinitions(currentOrg.id);
      setDefinitions(data);
    } catch (error) {
      console.error('Failed to load measurement definitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDefinitions = () => {
    let filtered = definitions;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((d) => d.category === selectedCategory);
    }

    // Filter by context
    if (selectedContext !== 'all') {
      filtered = filtered.filter((d) => d.context === selectedContext || d.context === 'both');
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (d) =>
          d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.unit?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredDefinitions(filtered);
  };

  const handleEdit = (definition: MeasurementDefinition) => {
    setEditingDefinition(definition);
    setShowDialog(true);
  };

  const handleDelete = async (definitionId: string) => {
    if (!currentOrg) return;
    if (!confirm('Are you sure you want to delete this measurement definition?')) return;

    try {
      await deleteMeasurementDefinition(currentOrg.id, definitionId);
      loadDefinitions();
    } catch (error) {
      console.error('Failed to delete measurement definition:', error);
      alert('Failed to delete measurement definition. It may be in use by templates.');
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditingDefinition(undefined);
    loadDefinitions();
  };

  const handleBackfill = async () => {
    if (!currentOrg) return;
    if (!confirm('This will import measurements from all completed inspections. This may take a few moments. Continue?')) {
      return;
    }

    setBackfilling(true);
    setBackfillResult(null);

    try {
      const result = await backfillMeasurementsFromInspections(currentOrg.id);
      setBackfillResult(result);
    } catch (error) {
      console.error('Failed to backfill measurements:', error);
      alert('Failed to backfill measurements. Please check the console for details.');
    } finally {
      setBackfilling(false);
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(definitions.map((d) => d.category || 'Uncategorized'))];

  // Get data type badge color
  const getDataTypeBadge = (dataType: string) => {
    const colors: Record<string, string> = {
      number: 'bg-blue-100 text-blue-800',
      text: 'bg-gray-100 text-gray-800',
      boolean: 'bg-green-100 text-green-800',
      date: 'bg-purple-100 text-purple-800',
      select: 'bg-yellow-100 text-yellow-800',
    };
    return colors[dataType] || 'bg-gray-100 text-gray-800';
  };

  // Get context badge color
  const getContextBadge = (context: string) => {
    const colors: Record<string, string> = {
      floc: 'bg-orange-100 text-orange-800',
      asset: 'bg-cyan-100 text-cyan-800',
      both: 'bg-indigo-100 text-indigo-800',
    };
    return colors[context] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading measurement definitions...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Measurement Definitions</h1>
          <p className="text-gray-600 mt-1">
            Create and manage reusable measurement definitions for consistency across templates
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleBackfill}
              disabled={backfilling}
              className="btn btn-secondary flex items-center justify-center"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${backfilling ? 'animate-spin' : ''}`} />
              {backfilling ? 'Importing...' : 'Import from History'}
            </button>
            <button
              onClick={() => {
                setEditingDefinition(undefined);
                setShowDialog(true);
              }}
              className="btn btn-primary flex items-center justify-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Definition
            </button>
          </div>
        )}
      </div>

      {/* Backfill Result */}
      {backfillResult && (
        <div className={`mb-6 p-4 rounded-lg ${
          backfillResult.errors.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'
        }`}>
          <h3 className={`text-sm font-semibold mb-2 ${
            backfillResult.errors.length > 0 ? 'text-yellow-900' : 'text-green-900'
          }`}>
            Import Complete
          </h3>
          <div className={`text-sm ${
            backfillResult.errors.length > 0 ? 'text-yellow-800' : 'text-green-800'
          }`}>
            <p>Processed {backfillResult.processedCount} inspections</p>
            <p>Recorded {backfillResult.measurementsRecorded} measurements</p>
            {backfillResult.errors.length > 0 && (
              <p className="mt-2 text-yellow-900 font-medium">
                {backfillResult.errors.length} inspection(s) had errors (check console for details)
              </p>
            )}
          </div>
          <button
            onClick={() => setBackfillResult(null)}
            className="mt-2 text-sm underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search definitions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="input w-full sm:w-48"
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category === 'all' ? 'All Categories' : category}
            </option>
          ))}
        </select>
        <select
          value={selectedContext}
          onChange={(e) => setSelectedContext(e.target.value)}
          className="input w-full sm:w-48"
        >
          <option value="all">All Contexts</option>
          <option value="floc">FLOC Only</option>
          <option value="asset">Asset Only</option>
          <option value="both">Both</option>
        </select>
      </div>

      {/* Definitions List */}
      {filteredDefinitions.length === 0 ? (
        <div className="card text-center py-12">
          <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery || selectedCategory !== 'all' || selectedContext !== 'all'
              ? 'No definitions found'
              : 'No measurement definitions yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || selectedCategory !== 'all' || selectedContext !== 'all'
              ? 'Try adjusting your filters'
              : 'Create reusable measurement definitions to ensure consistency across your templates'}
          </p>
          {!searchQuery && selectedCategory === 'all' && selectedContext === 'all' && canManage && (
            <button
              onClick={() => {
                setEditingDefinition(undefined);
                setShowDialog(true);
              }}
              className="btn btn-primary"
            >
              Create First Definition
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Context
                  </th>
                  {canManage && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDefinitions.map((definition) => (
                  <tr key={definition.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{definition.name}</div>
                        {definition.description && (
                          <div className="text-sm text-gray-500">{definition.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {definition.category || 'Uncategorized'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDataTypeBadge(
                          definition.dataType
                        )}`}
                      >
                        {definition.dataType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {definition.unit || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContextBadge(
                          definition.context
                        )}`}
                      >
                        {definition.context}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(definition)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                        >
                          <PencilIcon className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(definition.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="w-4 h-4 inline" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialog */}
      {showDialog && (
        <MeasurementDefinitionDialog
          definition={editingDefinition}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
}
